import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import DataTable from 'examples/Tables/DataTable';
import TopNavbar from 'examples/Navbars/TopNavbar';
import { useData } from '../../hooks';
import {
  apiGetBannerList,
  apiUploadBannerImage,
  apiCreateBanner,
  apiUpdateBanner,
  apiDeleteBanner,
} from '../../api/banner.api';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formLinkUrl, setFormLinkUrl] = useState('');
  const [formSortOrder, setFormSortOrder] = useState(0);
  const [formIsActive, setFormIsActive] = useState(true);
  const [formImageUrl, setFormImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);

  const { user, appIsReady } = useData();

  useEffect(() => {
    if (appIsReady && user) {
      fetchBanners(page);
    }
  }, [page, user, appIsReady]);

  const fetchBanners = async (pageNum) => {
    setLoading(true);
    try {
      const response = await apiGetBannerList({ page: pageNum, limit: 20 }, user.accessToken);
      if (response) {
        setBanners(response.data || []);
        setTotal(response.total || 0);
      }
    } catch (error) {
      console.error('Failed to load banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const imageUrl = await apiUploadBannerImage(file, user.accessToken);
      setFormImageUrl(imageUrl);
    } catch (error) {
      alert('이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingBanner(null);
    setFormTitle('');
    setFormLinkUrl('');
    setFormSortOrder(0);
    setFormIsActive(true);
    setFormImageUrl('');
    setDialogOpen(true);
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setFormTitle(banner.title || '');
    setFormLinkUrl(banner.linkUrl || '');
    setFormSortOrder(banner.sortOrder || 0);
    setFormIsActive(banner.isActive);
    setFormImageUrl(banner.imageUrl || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formImageUrl) {
      alert('이미지를 업로드해주세요.');
      return;
    }

    try {
      const body = {
        imageUrl: formImageUrl,
        linkUrl: formLinkUrl || undefined,
        title: formTitle || undefined,
        sortOrder: formSortOrder,
        isActive: formIsActive,
      };

      if (editingBanner) {
        await apiUpdateBanner(editingBanner.id, body, user.accessToken);
      } else {
        await apiCreateBanner(body, user.accessToken);
      }

      setDialogOpen(false);
      fetchBanners(page);
    } catch (error) {
      alert('저장에 실패했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('배너를 삭제하시겠습니까?')) return;

    try {
      await apiDeleteBanner(id, user.accessToken);
      fetchBanners(page);
    } catch (error) {
      alert('삭제에 실패했습니다.');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await apiUpdateBanner(banner.id, { isActive: !banner.isActive }, user.accessToken);
      fetchBanners(page);
    } catch (error) {
      alert('상태 변경에 실패했습니다.');
    }
  };

  const columns = [
    { Header: 'ID', accessor: 'id', align: 'left' },
    { Header: '미리보기', accessor: 'preview', align: 'center' },
    { Header: '제목', accessor: 'title', align: 'left' },
    { Header: '링크 URL', accessor: 'linkUrl', align: 'left' },
    { Header: '정렬순서', accessor: 'sortOrder', align: 'center' },
    { Header: '활성', accessor: 'isActive', align: 'center' },
    { Header: '관리', accessor: 'actions', align: 'center' },
  ];

  const rows = banners.map((banner) => ({
    id: banner.id,
    preview: (
      <img
        src={banner.imageUrl}
        alt={banner.title || '배너'}
        style={{ width: 120, height: 60, objectFit: 'cover', borderRadius: 4 }}
      />
    ),
    title: banner.title || '-',
    linkUrl: banner.linkUrl ? (
      <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
        {banner.linkUrl.length > 40 ? `${banner.linkUrl.slice(0, 40)}...` : banner.linkUrl}
      </a>
    ) : (
      '-'
    ),
    sortOrder: banner.sortOrder,
    isActive: (
      <Switch
        checked={banner.isActive}
        onChange={() => handleToggleActive(banner)}
        color="primary"
      />
    ),
    actions: (
      <MDBox display="flex" gap={1}>
        <IconButton size="small" onClick={() => openEditDialog(banner)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDelete(banner.id)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <TopNavbar />

      <MDBox display="flex" mr={2} justifyContent="right">
        <MDButton variant="gradient" color="info" onClick={openCreateDialog}>
          배너 추가
        </MDButton>
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  배너 이미지 관리
                </MDTypography>
              </MDBox>

              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  loading={loading}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingBanner ? '배너 수정' : '배너 추가'}</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="제목"
              fullWidth
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
            />
            <TextField
              label="링크 URL"
              fullWidth
              value={formLinkUrl}
              onChange={(e) => setFormLinkUrl(e.target.value)}
              placeholder="https://..."
            />
            <TextField
              label="정렬순서"
              type="number"
              fullWidth
              value={formSortOrder}
              onChange={(e) => setFormSortOrder(Number(e.target.value))}
            />
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDTypography variant="body2">활성 상태</MDTypography>
              <Switch
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
              />
            </MDBox>

            <MDBox>
              <MDTypography variant="body2" mb={1}>
                배너 이미지
              </MDTypography>
              <input type="file" accept="image/*" onChange={handleFileUpload} disabled={uploading} />
              {uploading && (
                <MDTypography variant="caption" color="info">
                  업로드 중...
                </MDTypography>
              )}
            </MDBox>

            {formImageUrl && (
              <MDBox>
                <MDTypography variant="caption" color="text">
                  미리보기:
                </MDTypography>
                <img
                  src={formImageUrl}
                  alt="미리보기"
                  style={{ width: '100%', maxHeight: 200, objectFit: 'contain', marginTop: 8, borderRadius: 4 }}
                />
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>취소</Button>
          <Button onClick={handleSave} variant="contained" color="primary" disabled={uploading}>
            저장
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
};

export default BannerManagement;
